import useAnalytics from '../../hooks/useAnalytics';
import { Container, FreePlanIndicator, UpgradeButton } from './TierBanner.styles';
import { GetAccountInfoResponse } from '@hud/portal-dtos/webapp-common.dto';
import { getPlugSDK } from '../../hooks/usePlugSDK';

interface TierBannerProps {
  accountInfo: GetAccountInfoResponse;
}

function FreeTierText({
  liveSessions,
  throttledSessions,
  maxLiveSessions,
}: {
  liveSessions: number;
  throttledSessions: number;
  maxLiveSessions: number;
}) {
  if (throttledSessions)
    return (
      <div>
        <b>{liveSessions}</b>/<b>{maxLiveSessions}</b> concurrent services. Data dropped
        from <b>{throttledSessions.toLocaleString()}</b>{' '}
        {throttledSessions === 1 ? 'service' : 'services'} this month.
      </div>
    );

  if (liveSessions)
    return (
      <div>
        Running{' '}
        <b>
          {liveSessions}/{maxLiveSessions}
        </b>{' '}
        concurrent services. Need more?
      </div>
    );

  return (
    <div>
      Up to <b>{maxLiveSessions}</b> concurrent services. Need more?
    </div>
  );
}

function EnterpriseTierText({ throttledSessions }: { throttledSessions: number }) {
  return (
    <div>
      Services limit exceeded, data dropped from{' '}
      <b>{throttledSessions.toLocaleString()}</b>{' '}
      {throttledSessions === 1 ? 'session' : 'sessions'} this month.
    </div>
  );
}

export function TierBanner({ accountInfo }: TierBannerProps) {
  const analytics = useAnalytics();

  const tier = accountInfo.tier;

  const liveSessions = accountInfo.tierData?.liveSessions ?? 0;
  const maxLiveSessions = accountInfo.tierData?.maxLiveSessions ?? 10;
  const throttledSessions = accountInfo.tierData?.throttledSessions ?? 0;

  const isExceedingFreePlanLimit = throttledSessions > 0;

  const upgradeButtonOnClick =
    tier === 'free'
      ? () => {
          analytics.capture('upgrade_tier_button_clicked');
          window.open('https://www.hud.io/pricing');
        }
      : () => {
          analytics.capture('contact_us_to_upgrade_button_clicked');
          getPlugSDK()?.toggleWidget?.(true, 'create_conversation', {
            startConversationContent:
              `Hi, I've noticed that some of my services are hitting their data limits. Could you help me explore upgrade options?`,
          });
        };

  return (
    <Container exceededLimit={isExceedingFreePlanLimit}>
      {tier === 'free' && (
        <FreePlanIndicator limitReached={isExceedingFreePlanLimit}>
          Free Plan
        </FreePlanIndicator>
      )}

      {tier === 'free' ? (
        <FreeTierText
          liveSessions={liveSessions}
          throttledSessions={throttledSessions}
          maxLiveSessions={maxLiveSessions}
        />
      ) : (
        <EnterpriseTierText throttledSessions={throttledSessions} />
      )}

      <UpgradeButton
        onClick={upgradeButtonOnClick}
        limitReached={isExceedingFreePlanLimit}
      >
        {tier === 'free' ? 'Upgrade' : 'Contact Us to Upgrade'}
      </UpgradeButton>
    </Container>
  );
}
